import { useState } from "react";
import { formatBytes } from "./formatBytes";
import { FilePicker } from "./FilePicker";
import { RemoteFirmwareFile, isRemoteFirmwareFile } from "../core/files/RemoteFirmwareFile";

export type ServerFirmwareKind = "v3-package" | "v2-package" | "v3-partition" | "v2-partition";

export interface ServerFirmwareEntry {
  fileId: string;
  kind: ServerFirmwareKind;
  name: string;
  size: number;
  lastModified: number;
  etag: string;
  /** Relative folder below the server catalog root. */
  folder?: string;
}

interface Props {
  accept: string;
  label: string;
  kind: ServerFirmwareKind;
  file?: File;
  files?: File[];
  onChange: (file?: File) => void;
  onFilesChange?: (files: File[]) => void;
  multiple?: boolean;
  compact?: boolean;
}

function dateLabel(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" });
}

function folderLabel(folder: string): string {
  return folder || "Thư mục gốc";
}

export function ServerFirmwarePicker({ accept, label, kind, file, files, onChange, onFilesChange, multiple = false, compact = false }: Props) {
  const [mode, setMode] = useState<"local" | "server">("local");
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<ServerFirmwareEntry[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [selectedFolder, setSelectedFolder] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const remoteFiles = (files?.filter(isRemoteFirmwareFile) ?? (file && isRemoteFirmwareFile(file) ? [file] : []));
  const isPartition = kind.endsWith("-partition");

  const loadEntries = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`/__firmware/files?kind=${encodeURIComponent(kind)}`, { credentials: "same-origin" });
      const payload = await response.json().catch(() => ({})) as { files?: ServerFirmwareEntry[]; error?: string };
      if (!response.ok) throw new Error(payload.error === "FIRMWARE_SERVER_NOT_CONFIGURED" ? "Server chưa cấu hình thư mục firmware." : payload.error ?? `Không tải được catalog (HTTP ${response.status}).`);
      const nextEntries = payload.files ?? [];
      setEntries(nextEntries);
      setSelectedIds(new Set(remoteFiles.map((remote) => remote.remoteMetadata.fileId)));
      setSelectedFolder((current) => current && nextEntries.some((entry) => (entry.folder ?? "") === current) ? current : "");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : String(loadError));
    } finally {
      setLoading(false);
    }
  };

  const openServer = () => {
    setMode("server");
    setOpen(true);
    void loadEntries();
  };

  const applyServerSelection = () => {
    const selected = entries.filter((entry) => selectedIds.has(entry.fileId));
    if (!selected.length) return;
    const nextFiles = selected.map((entry) => new RemoteFirmwareFile(entry));
    if (multiple) onFilesChange?.(nextFiles);
    onChange(nextFiles[0]);
    setOpen(false);
  };

  const folderOptions = Array.from(new Set(entries.map((entry) => entry.folder ?? "")))
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
  const normalizedQuery = query.toLocaleLowerCase().trim();
  const visibleEntries = entries.filter((entry) => {
    const matchesFolder = !isPartition || !selectedFolder || (entry.folder ?? "") === selectedFolder;
    return matchesFolder && entry.name.toLocaleLowerCase().includes(normalizedQuery);
  });
  const selectedSummary = remoteFiles.length === 1
    ? `${remoteFiles[0].remoteMetadata.folder ? `${remoteFiles[0].remoteMetadata.folder}/` : ""}${remoteFiles[0].name} · ${formatBytes(remoteFiles[0].size)}`
    : remoteFiles.length > 1 ? `${remoteFiles.length} file server đã chọn` : "Chưa chọn file server";

  return (
    <div className="firmware-source-picker">
      <div className="firmware-source-tabs" role="group" aria-label={`Nguồn ${label}`}>
        <button type="button" className={mode === "local" ? "secondary is-active" : "secondary"} aria-pressed={mode === "local"} onClick={() => setMode("local")}>Máy này</button>
        <button type="button" className={mode === "server" ? "secondary is-active" : "secondary"} aria-pressed={mode === "server"} onClick={openServer}>Server</button>
      </div>
      {mode === "local" ? (
        <FilePicker compact={compact} label={label} accept={accept} file={file && !isRemoteFirmwareFile(file) ? file : undefined} files={files?.filter((candidate) => !isRemoteFirmwareFile(candidate))} multiple={multiple} onChange={(nextFile) => { setMode("local"); onChange(nextFile); }} onFilesChange={(nextFiles) => { setMode("local"); onFilesChange?.(nextFiles); }} />
      ) : (
        <div className={`file-picker file-picker--compact firmware-server-selected${compact ? "" : " firmware-server-selected--regular"}`}>
          <span className="file-picker-label">{label} · Server</span>
          <span className="file-picker-main"><strong>{selectedSummary}</strong><button type="button" className="file-picker-action" onClick={openServer}>Chọn file</button></span>
        </div>
      )}
      {open && <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) setOpen(false); }}>
        <section className="dialog firmware-server-dialog" role="dialog" aria-modal="true" aria-label={`Chọn ${label} trên server`}>
          <div className="firmware-server-dialog-heading"><div><h2>Chọn file trên server</h2><p>{kind} · {entries.length} file</p></div><button type="button" className="secondary" onClick={() => setOpen(false)}>Đóng</button></div>
          <div className="firmware-server-toolbar">
            <input className="text-input" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Tìm tên file…" aria-label="Tìm file server" />
            {isPartition && <select className="firmware-server-folder" value={selectedFolder} onChange={(event) => setSelectedFolder(event.target.value)} aria-label="Thư mục firmware server">
              <option value="">Tất cả thư mục</option>
              {folderOptions.map((folder) => <option key={folder || "."} value={folder}>{folderLabel(folder)}</option>)}
            </select>}
            <button type="button" className="secondary" onClick={() => void loadEntries()} disabled={loading}>{loading ? "Đang quét…" : "Quét lại"}</button>
          </div>
          {error && <p className="error-message" role="alert">{error}</p>}
          {!loading && !error && visibleEntries.length === 0 && <p className="muted small">Chưa có file phù hợp.</p>}
          <div className="firmware-server-list">
            {visibleEntries.map((entry) => {
              const checked = selectedIds.has(entry.fileId);
              return <label className="firmware-server-row" key={entry.fileId}>
                <input type={multiple ? "checkbox" : "radio"} name={`server-${kind}`} checked={checked} onChange={() => setSelectedIds((current) => multiple ? new Set(current.has(entry.fileId) ? [...current].filter((id) => id !== entry.fileId) : [...current, entry.fileId]) : new Set([entry.fileId]))} />
                <span className="firmware-server-file"><strong title={entry.name}>{entry.name}</strong><small>{folderLabel(entry.folder ?? "")} · {formatBytes(entry.size)} · {dateLabel(entry.lastModified)}</small></span>
              </label>;
            })}
          </div>
          <div className="firmware-server-dialog-actions"><span className="muted small">{selectedIds.size} file đã chọn</span><button type="button" className="primary" disabled={!selectedIds.size || loading} onClick={applyServerSelection}>Chọn</button></div>
        </section>
      </div>}
    </div>
  );
}
